import { ITheme, IStyle, mergeStyleSets, getTheme, PanelType, ActionButton } from "@fluentui/react";
import { useState, useMemo } from "react";
import { BwjButtonType } from "../../../framework/components/bwj-button";
import { BwjGrid } from "../../../framework/components/bwj-layout";
import { RowCol } from "../../../framework/components/bwj-layout/rowcol";
import { Spacing } from "../../../framework/components/bwj-layout/styles";
import { BwjSidePanel } from "../../../framework/components/bwj-side-panel";
import { CustomerAccountService, useProjectConfiguration } from "../../../services/customer-account";
import { useEventNotification } from "../../../framework/services/event-notification";
import { ShopAppEvents } from "../../events";
import { firstOrDefault } from "../../../framework/utils/collection";
import { isMeaningfulString } from "../../../framework/utils/string";

const getStyleClasses = (theme: ITheme) => {
  const main: IStyle = {
    padding: 20,
    paddingTop: 0,
  };

  const accountContainer: IStyle = {
    paddingBottom: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.neutralLight}`
  };

  const initialBadge: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: 150,
    height: 150,
    borderRadius: 75,
    backgroundColor: theme.palette.themePrimary,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
  };

  const initials: IStyle = {
    width: '90%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'center',
    color: theme.palette.white,
    fontSize: 32,
    fontWeight: 600,
    margin: 0,
    padding: 0,
    letterSpacing: '1px',
  };

  const name: IStyle = {
    margin: 0,
    marginTop: 15,
    fontSize: theme.fonts.xLarge.fontSize,
    lineHeight: theme.fonts.xLarge.lineHeight,
    fontWeight: 600,
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'center',
  };

  const projectDescription: IStyle = {
    marginTop: 10,
    fontSize: theme.fonts.medium.fontSize,
    lineHeight: theme.fonts.medium.lineHeight,
    fontWeight: 'normal',
    color: theme.palette.neutralSecondary,
    width: '100%',
    textAlign: 'center',
  };

  return mergeStyleSets({
    main,
    accountContainer,
    initialBadge,
    initials,
    name,
    projectDescription,
  });
};

const subscriberId = 'Panel.CustomerAccount';

export const CustomerAccountPanel: React.FunctionComponent = () => {
  const [showPanel, setShowPanel] = useState(false);
  const projectConfig = useProjectConfiguration(subscriberId);
  const theme = getTheme();
  const styles = getStyleClasses(theme);
  const initials = useMemo(() => {
    // only regard spaces and dashes as letter separators
    const source = projectConfig.myName.replaceAll(/[^a-z0-9\s-]+/ig, '');
    const initials = source.split(/[\s-]+/g)
    .map(name => name[0]);

    return initials.join('');
  }, [projectConfig]);

  const title = useMemo(() => {
    const homeowner = projectConfig.myName.trim();
    const lastLetter = homeowner[homeowner.length - 1];
    return lastLetter === 's' ? `${homeowner}' House` : `${homeowner}'s House`;
  }, [projectConfig]);

  const subtitle = useMemo(() => {
    const sites = projectConfig.installLocations.map(i => i.name).join(', ');
    const tense = projectConfig.installLocations.length > 1 ? 's' : '';
    return `Storm door${tense} (${sites})`;
  }, [projectConfig]);

  const badgeStyle = useMemo(() => {
    const img = firstOrDefault(projectConfig.installLocations, i => isMeaningfulString(i.thumbnailUrl))?.thumbnailUrl;
    return !!img ? { backgroundImage: `url("${img}")` } : undefined;
  }, [projectConfig]);

  useEventNotification(() => setShowPanel(true), [], ShopAppEvents.ShowProjectAccountPanel, subscriberId);

  const onCloseThisCatalog = () => {
    CustomerAccountService.signOut();
    setShowPanel(false);
  };

  return (
    <BwjSidePanel
      showPanel={showPanel}
      panelType={PanelType.smallFixedFar}
      footerButtons={[{
        buttonStyle: BwjButtonType.DefaultStandard,
        text: 'Close',
        onClick: () => setShowPanel(false),
      }]}
      onDismiss={() => setShowPanel(false)}
      closeButtonAriaLabel={'Close customer account panel'}
    >
      <div className={styles.main}>
        <BwjGrid>
          <RowCol bottomSpacing={Spacing.Medium}>
            <section className={styles.accountContainer}>
              <div className={styles.initialBadge} style={badgeStyle}>
                {!badgeStyle &&
                <h1 className={styles.initials}>{initials}</h1>}
              </div>
              <h2 className={styles.name}>{title}</h2>
              <p className={styles.projectDescription}>{subtitle}</p>
            </section>
          </RowCol>
          <RowCol bottomSpacing={Spacing.Medium}>
            <ActionButton
              iconProps={{ iconName: 'SignOut'}}
              onClick={onCloseThisCatalog}
            >
              {'Close this catalog'}
            </ActionButton>
          </RowCol>
        </BwjGrid>
      </div>
    </BwjSidePanel>
  );
};